import * as React from 'react';
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from '@mui/material/styles';
import {
  ThemeContext,
  useTheme,
} from 'application/context/PermissionsContext/ThemeContext/ThemeContext';
import { createMuiTheme } from 'application/context/PermissionsContext/ThemeContext/muiTheme';
import { Theme } from 'application/context/PermissionsContext/ThemeContext/themesTypes';

// FieldInspect values for the v1 theme keys (matching the v2 tokens in
// globals.css), so v1 screens embedded in v2 pages lose the Paiv orange.
const lightTheme: Theme = {
  isDark: false,
  primary: '#157A35',
  primaryDark: '#0F5C27',
  primaryLight: '#CFEFD8',
  secondary: '#157A35',
  secondaryDark: '#0F5C27',
  secondaryLight: '#CFEFD8',
  background1: '#FFFFFF',
  background2: '#F5F5F1',
  background3: '#ECEEE9',
  text: '#111311',
  textLight: '#5C5F5C',
  textCTA: '#FFFFFF',
  link: '#157A35',
  // fill1-3 are MUI's action.active / hover / selected colors
  fill1: '#5C5F5C',
  fill2: '#F4F7F3',
  fill3: '#CFEFD8',
  stroke: '#E1E3DE',
};

const darkTheme: Theme = {
  isDark: true,
  primary: '#5FD884',
  primaryDark: '#22B04B',
  primaryLight: '#1D3A2B',
  secondary: '#5FD884',
  secondaryDark: '#22B04B',
  secondaryLight: '#1D3A2B',
  background1: '#050B07',
  background2: '#0B120D',
  background3: '#1C2A22',
  text: '#FFFFFF',
  textLight: '#A9B0AA',
  textCTA: '#050B07',
  link: '#5FD884',
  fill1: '#A9B0AA',
  fill2: '#1C2A22',
  fill3: '#1D3A2B',
  stroke: '#2A2E2B',
};

// v1's MUI theme sets Manrope on every text variant; use the v2 font instead.
const FONT = 'Geist, "Helvetica Neue", Helvetica, Arial, sans-serif';
const typography = Object.fromEntries(
  [
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'subtitle1',
    'subtitle2',
    'body1',
    'body2',
    'button',
    'caption',
    'overline',
  ].map((variant) => [variant, { fontFamily: FONT }])
);

export function FieldInspectLegacyTheme({
  children,
}: {
  children: React.ReactNode;
}) {
  const { theme, toggleTheme } = useTheme();
  const fiTheme = theme.isDark ? darkTheme : lightTheme;

  const muiTheme = React.useMemo(
    () =>
      createTheme(createMuiTheme(fiTheme), {
        typography: { fontFamily: FONT, ...typography },
        components: {
          MuiButton: {
            styleOverrides: {
              // v1 colors every button's text with textCTA; keep plain
              // buttons readable, and make primary buttons ink like v2.
              root: {
                color: fiTheme.text,
                '&:hover': { backgroundColor: fiTheme.fill2 },
              },
              containedPrimary: {
                backgroundColor: fiTheme.isDark ? '#FFFFFF' : '#111311',
                color: fiTheme.isDark ? '#111311' : '#FFFFFF',
                '&:hover': {
                  backgroundColor: fiTheme.isDark ? '#DCE6DE' : '#262A27',
                },
              },
            },
          },
        },
      }),
    [fiTheme]
  );

  return (
    <ThemeContext.Provider value={{ theme: fiTheme, toggleTheme }}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
